import React, {useCallback, useContext, useMemo} from 'react';

import {ProfileMain} from 'src/components/profile-tab-structure';
import {SaveWithReasonButton} from 'src/components/save-with-reason/save-with-reason-button';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {DISCARD_CHANGES_SUCCESS_MESSAGE, SAVE_CHANGES_SUCCESS_MESSAGE} from 'src/constants';
import {MessageContext} from 'src/contexts/message-context';
import {ValidationContextProvider} from 'src/contexts/validation-context/validation-context-provider';
import {useAdminAccessRight} from 'src/hooks/access-rights';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {UserLimitsController} from 'src/modules/profile/account/create-user/steps/user-limits';


import Button from '@pb/kit-component-button';
import {Group, Item} from '@pb/kit-component-group';
import Typography from '@pb/kit-component-typography';

import {LimitsTabs} from './limits-tabs';
import {useUsedLimits} from './useUsedLimits';

import type {MessageContextType} from 'src/contexts/message-context';


type Props = {
    userId: number,
    accountId: number,
    isConnectorLimitsAllowed: boolean,
};

export const UserLimits = ({userId, accountId, isConnectorLimitsAllowed}: Props) => {
    const {setSuccessMessage} = useContext<MessageContextType>(MessageContext);
    const {canEditUserLimits} = useAdminAccessRight();
    const {userFieldChange, userSubmit} = useEventTracking();

    const {
        isLoading: isUsedLimitsLoading,
        loadingError: usedLimitsLoadingError,
        data: usedLimits,
    } = useUsedLimits({userId});

    const onProceed = useCallback(() => {
        userSubmit({place: 'User Limits', submit: true});
        setSuccessMessage(SAVE_CHANGES_SUCCESS_MESSAGE);
    }, [setSuccessMessage, userSubmit]);

    const onFieldChange = (parameterName: string) => {
        userFieldChange({
            section: 'Limits',
            parameterName,
            viewType: 'Existed',
        });
    };

    const editDisabled = useMemo(() => !canEditUserLimits(accountId), [accountId, canEditUserLimits]);

    return (
        <>
            <LimitsTabs userId={userId} activeTab="limits" isConnectorLimitsAllowed={isConnectorLimitsAllowed}/>
            <ValidationContextProvider>
                <UserLimitsController
                    userId={userId}
                    onFieldChange={onFieldChange}
                    onProceed={onProceed}
                    usedLimits={usedLimits}
                    editDisabled={editDisabled}>
                    {({busy, isLoading, loadingError, content, onSave, onDiscard, isChunkChanged}) => (
                        <ServerResponseHandler serverError={loadingError || usedLimitsLoadingError} sectionName="Limits">
                            <ProfileMain header={(
                                <Item grow={1}>
                                    <Group justify="space-between">
                                        <Typography
                                            fontWeight="semi-bold"
                                            variant="heading-level-3">
                                            Limits
                                        </Typography>
                                        {!editDisabled && (
                                            <Group>
                                                <Button
                                                    variant="Type.SECONDARY"
                                                    onClick={() => {
                                                        userSubmit({place: 'User Limits', submit: false});
                                                        onDiscard();
                                                        setSuccessMessage(DISCARD_CHANGES_SUCCESS_MESSAGE);
                                                    }}
                                                    disabled={!isChunkChanged || isLoading || isUsedLimitsLoading}>
                                                    Discard Changes
                                                </Button>
                                                <SaveWithReasonButton
                                                    busy={busy}
                                                    disabled={!isChunkChanged || isLoading || isUsedLimitsLoading}
                                                    onSave={onSave}
                                                />
                                            </Group>
                                        )}
                                    </Group>
                                </Item>
                            )}>
                                {content}
                            </ProfileMain>
                        </ServerResponseHandler>
                    )}
                </UserLimitsController>
            </ValidationContextProvider>
        </>
    );
};
